import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from '@/i18n';
import { useTransportQuery } from '@/hooks/queries/useTransportQuery';
import { useComputedDrivingEta } from '@/hooks/useComputedDrivingEta';
import { useTrackingNumber } from '@/hooks/useTrackingNumber';
import Map from '@/components/Map';
import { Footer } from '@/components/Footer';
import { TrackHeader } from '@/components/TrackHeader';
import { TTransport } from '@/types/models';
import { formatTransportEta } from '@/utils/formatTransportEta';
import {
  PortalLink,
  DriverBox,
  CtaButton,
  TimelineStep,
  ProgressCircle,
  caseSelector,
  TRACKING_CASES,
  getContentBoxes,
  getMapDestinationCoordinates,
  RequirementsModal,
  RescheduleDrawer,
  ShipmentDetails,
  TrackSectionBar,
  TrackSectionPanel,
  type TrackSection,
} from '@/components/TrackingDetail';
import { DriverLocation } from '@/components/TrackingDetail/websocket';
import { TrackingNotFound } from '@/pages/TrackingNotFound';

const Track = () => {
  const trackingNumber = useTrackingNumber();
  const { dateLocale, locale, t } = useTranslation();

  const [rescheduleMode, setRescheduleMode] = useState<
    'pickup' | 'delivery' | null
  >(null);

  const [stopPolling, setStopPolling] = useState(false);

  const { data, isError, isLoading } = useTransportQuery({
    trackingNumber,
    refetchInterval: stopPolling ? false : undefined,
  });

  const [acceptedInSession, setAcceptedInSession] = useState(false);
  const [driverLocation, setDriverLocation] = useState<DriverLocation | null>(
    null
  );
  const [activeSection, setActiveSection] = useState<TrackSection>('status');

  const handleDriverLocationChange = useCallback(
    (location: DriverLocation | null) => {
      setDriverLocation(location);
    },
    []
  );

  const handleAcceptRequirements = () => {
    setAcceptedInSession(true);
  };

  useEffect(() => {
    setAcceptedInSession(false);
    setStopPolling(false);
    setActiveSection('status');
  }, [trackingNumber]);

  useEffect(() => {
    if (isError) {
      setStopPolling(true);
    }
  }, [isError]);

  const transport_data: TTransport | null = useMemo(() => {
    if (data?.success && data.transport) {
      return data.transport;
    }
    return null;
  }, [data]);

  const selectedCase = useMemo(() => {
    return caseSelector(transport_data);
  }, [transport_data]);

  useEffect(() => {
    setDriverLocation(null);
  }, [trackingNumber, selectedCase]);

  const mapDestinationCoordinates = useMemo(
    () => getMapDestinationCoordinates(selectedCase, transport_data),
    [selectedCase, transport_data]
  );

  const apiEta = useMemo(() => {
    if (!transport_data) return null;
    return formatTransportEta(transport_data.interval, transport_data.eta);
  }, [transport_data]);

  const shouldComputeDrivingEta =
    selectedCase === TRACKING_CASES.outForPickup ||
    selectedCase === TRACKING_CASES.outForDelivery;

  const computedDrivingEta = useComputedDrivingEta({
    enabled: shouldComputeDrivingEta,
    hasApiEta: Boolean(apiEta),
    driverLocation,
    destinationLatitude: mapDestinationCoordinates.latitude,
    destinationLongitude: mapDestinationCoordinates.longitude,
    dateLocale,
  });

  const contentBoxes = useMemo(() => {
    return getContentBoxes(selectedCase, transport_data, t, dateLocale, {
      computedEta: computedDrivingEta,
    });
  }, [selectedCase, transport_data, t, dateLocale, locale, computedDrivingEta]);

  const mapDriverId = transport_data?.driver?.uuid ?? null;

  const CTABUTTONS = useMemo(
    () => [
      {
        text: t('cta.postponePickup'),
        onClick: () => setRescheduleMode('pickup'),
        color: '#D44942',
        secondaryColor: '#FFEDEC',
        cases: [TRACKING_CASES.registered],
      },
      {
        text: t('cta.postponeDelivery'),
        onClick: () => setRescheduleMode('delivery'),
        color: '#D44942',
        secondaryColor: '#FFEDEC',
        cases: [
          TRACKING_CASES.arrivalAtDepot,
          TRACKING_CASES.departureFromDepot,
        ],
      },
      {
        text: t('cta.downloadLabel'),
        onClick: () => {
          if (transport_data?.label_url) {
            window.open(
              transport_data.label_url,
              '_blank',
              'noopener,noreferrer'
            );
          }
        },
        color: '#4242D4',
        secondaryColor: '#007AFFAB',
        cases: [TRACKING_CASES.registered],
        requiresLabelUrl: true,
      },
    ],
    [t, transport_data?.label_url]
  );

  const visibleCtaButtons = useMemo(
    () =>
      CTABUTTONS.filter((button) => {
        if (!button.cases) return false;
        if (
          'requiresLabelUrl' in button &&
          button.requiresLabelUrl &&
          !transport_data?.label_url
        ) {
          return false;
        }
        return button.cases.includes(selectedCase || '');
      }),
    [CTABUTTONS, selectedCase, transport_data?.label_url]
  );

  const hasTermsAcceptance = Boolean(transport_data?.terms_acceptance);

  if (isLoading) return null;

  if (isError || !transport_data) {
    return <TrackingNotFound />;
  }

  const showRequirementsModal = !hasTermsAcceptance && !acceptedInSession;

  return (
    <div className='flex min-h-[100dvh] w-full flex-1 flex-col bg-[#f5f7f8]'>
      <TrackHeader />
      <div className='flex w-full flex-1 flex-col items-center'>
        {/* Requirements Modal */}
        <RequirementsModal
          isOpen={showRequirementsModal}
          onAccept={handleAcceptRequirements}
          trackingNumber={trackingNumber}
        />

        {/* Reschedule Drawer */}
        <RescheduleDrawer
          isOpen={rescheduleMode !== null}
          onClose={() => setRescheduleMode(null)}
          currentDate={
            rescheduleMode === 'pickup'
              ? (transport_data as any)?.pickup_date ?? null
              : (transport_data as any)?.delivery_date ?? null
          }
          mode={rescheduleMode ?? 'pickup'}
          trackingNumber={trackingNumber}
        />

        <div className='flex w-full flex-col items-center justify-center'>
          {/* MOBILE + TABLET LAYOUT */}
          <div className='block w-full xl:hidden'>
            {/* Map Section - full height, not clipped */}
            <div className='track-mobile-map relative h-[45vh] w-full'>
              <div className='absolute inset-x-0 bottom-[80px] top-0 overflow-hidden'>
                <Map
                  driverId={mapDriverId}
                  recipientLatitude={mapDestinationCoordinates.latitude}
                  recipientLongitude={mapDestinationCoordinates.longitude}
                  selectedCase={selectedCase}
                  trackingNumber={trackingNumber}
                  onDriverLocationChange={handleDriverLocationChange}
                />
              </div>
            </div>

            {/* White content area — circle sits at the boundary */}
            <div className='relative z-[2] -mt-[80px] w-full bg-[#f5f7f8] pb-8'>
              <div className='pointer-events-none absolute left-1/2 top-2 z-20 -translate-x-1/2 -translate-y-1/2'>
                <ProgressCircle selectedCase={selectedCase || ''} size={160} />
              </div>

              <div className='relative z-10 mx-auto w-full max-w-[400px] px-4 pt-[100px]'>
                <div className='relative mx-auto mt-4 flex w-full max-w-[420px] items-stretch justify-center gap-2 px-4'>
                  <PortalLink trackingNumber={trackingNumber} isMobile />
                  <DriverBox
                    selectedCase={selectedCase || ''}
                    transport_data={transport_data || ({} as TTransport)}
                    isMobile
                  />
                </div>

                {visibleCtaButtons.length > 0 ? (
                  <div className='mt-4 flex w-full flex-col items-center justify-center gap-3'>
                    {visibleCtaButtons.map((button) => (
                      <CtaButton
                        key={button.text}
                        color={button.color}
                        icon='calendar'
                        isMobile
                        onClick={button.onClick}
                        secondaryColor={button.secondaryColor}
                        text={button.text}
                      />
                    ))}
                  </div>
                ) : null}

                <div className='mt-5'>
                  <TrackSectionBar
                    activeSection={activeSection}
                    onChange={setActiveSection}
                  />
                </div>

                {activeSection === 'status' ? (
                  <TrackSectionPanel sectionKey='status'>
                    <div className='mt-5 flex w-full flex-col items-start justify-start gap-0'>
                      {contentBoxes.map((box, index) => {
                        const activeStepIndex = contentBoxes.findIndex(
                          (item) => item.selected
                        );
                        const title =
                          typeof box.title === 'function'
                            ? box.title(transport_data)
                            : box.title;
                        const description =
                          typeof box.description === 'function'
                            ? box.description(transport_data)
                            : box.description;

                        return (
                          <TimelineStep
                            key={box.index}
                            description={description}
                            index={box.index}
                            isMobile
                            selected={box.selected}
                            showConnector={index < contentBoxes.length - 1}
                            title={title as string}
                            backgroundColor={box.backgroundColor}
                            completed={
                              activeStepIndex !== -1 && index < activeStepIndex
                            }
                            connectorCompleted={
                              activeStepIndex !== -1 && index < activeStepIndex
                            }
                          />
                        );
                      })}
                    </div>
                  </TrackSectionPanel>
                ) : (
                  <TrackSectionPanel sectionKey='info'>
                    <div className='mt-5'>
                      <ShipmentDetails embedded transport={transport_data} />
                    </div>
                  </TrackSectionPanel>
                )}
              </div>
            </div>
          </div>

          {/* DESKTOP LAYOUT */}
          <div className='hidden w-full xl:block'>
            <div className='relative w-full'>
              <div className='relative h-[400px] w-full overflow-hidden shadow-[0_16px_48px_rgba(9,45,54,0.14)]'>
                <Map
                  driverId={mapDriverId}
                  recipientLatitude={mapDestinationCoordinates.latitude}
                  recipientLongitude={mapDestinationCoordinates.longitude}
                  selectedCase={selectedCase}
                  trackingNumber={trackingNumber}
                  onDriverLocationChange={handleDriverLocationChange}
                />
              </div>

              <div className='pointer-events-none absolute bottom-[-135px] left-1/2 z-20 flex w-full max-w-[70%] -translate-x-1/2 items-center justify-between px-8 min-[1400px]:bottom-[-155px]'>
                <div className='pointer-events-auto flex min-w-0 items-center gap-4'>
                  <PortalLink trackingNumber={trackingNumber} />
                  <DriverBox
                    selectedCase={selectedCase || ''}
                    transport_data={transport_data}
                  />
                </div>

                <div className='shrink-0 min-[1400px]:hidden'>
                  <ProgressCircle
                    selectedCase={selectedCase || ''}
                    size={280}
                  />
                </div>
                <div className='hidden shrink-0 min-[1400px]:block'>
                  <ProgressCircle selectedCase={selectedCase || ''} />
                </div>
              </div>
            </div>

            <div className='relative z-[2] bg-[#f5f7f8]'>
              <div className='mx-auto w-full max-w-[70%] px-8 pb-14'>
                <div className='flex items-start justify-between gap-8'>
                  <div className='min-w-0 flex-1 pt-10'>
                    <TrackSectionBar
                      activeSection={activeSection}
                      onChange={setActiveSection}
                    />

                    {activeSection === 'status' ? (
                      <TrackSectionPanel className='mt-6' sectionKey='status'>
                        <div className='flex w-full flex-col items-start justify-start gap-0'>
                          {contentBoxes.map((box, index) => {
                            const activeStepIndex = contentBoxes.findIndex(
                              (item) => item.selected
                            );
                            const title =
                              typeof box.title === 'function'
                                ? box.title(transport_data)
                                : box.title;
                            const description =
                              typeof box.description === 'function'
                                ? box.description(transport_data)
                                : box.description;

                            return (
                              <TimelineStep
                                key={box.index}
                                description={description}
                                index={box.index}
                                selected={box.selected}
                                showConnector={index < contentBoxes.length - 1}
                                title={title as string}
                                backgroundColor={box.backgroundColor}
                                completed={
                                  activeStepIndex !== -1 &&
                                  index < activeStepIndex
                                }
                                connectorCompleted={
                                  activeStepIndex !== -1 &&
                                  index < activeStepIndex
                                }
                              />
                            );
                          })}
                        </div>
                      </TrackSectionPanel>
                    ) : (
                      <TrackSectionPanel className='mt-6' sectionKey='info'>
                        <div className='max-w-xl'>
                          <ShipmentDetails
                            embedded
                            transport={transport_data}
                          />
                        </div>
                      </TrackSectionPanel>
                    )}
                  </div>

                  <div className='flex w-[320px] shrink-0 flex-col items-stretch gap-3 self-start pt-52 min-[1400px]:pt-56'>
                    {visibleCtaButtons.length > 0
                      ? visibleCtaButtons.map((button) => (
                          <CtaButton
                            key={button.text}
                            color={button.color}
                            icon='calendar'
                            onClick={button.onClick}
                            secondaryColor={button.secondaryColor}
                            text={button.text}
                          />
                        ))
                      : null}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
};

export { Track };
